/**
 * The household's wallpaper on the web (docs/upgrades.md "Wallpaper"): the board summary names it
 * (`wallpaper`, the highest built), this keeps the last one seen (remembered on the device, so a
 * load paints it at once), and the shell draws it behind every screen while `wallpaperUI` is on, by
 * setting `data-wallpaper` on the body (src/styles.css). Flag off, or nothing built: the board's own
 * stone, as before. No imports from the api, so src/api.ts can feed it.
 */
import { useEffect, useState } from "preact/hooks";
import { shownWallpaper, wallpaperOf, type WallpaperKey } from "../shared/upgrades.ts";

const KEY = "op.wallpaper";
const EVENT = "op:wallpaper";

function read(): WallpaperKey | null {
  try {
    return wallpaperOf(localStorage.getItem(KEY))?.key ?? null;
  } catch {
    return null;
  }
}

let current: WallpaperKey | null = typeof localStorage === "undefined" ? null : read();

/** The household's wallpaper as last seen (any flag). */
export function householdWallpaper(): WallpaperKey | null {
  return current;
}

/** A board summary (or a pledge that built one) names the household's wallpaper. Absent is left alone. */
export function noteWallpaper(value: string | null | undefined): void {
  if (value === undefined) return;
  const next = wallpaperOf(value)?.key ?? null;
  if (next === current) return;
  current = next;
  try {
    if (next) localStorage.setItem(KEY, next);
    else localStorage.removeItem(KEY);
  } catch {
    // Private mode: it lives for the page.
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
}

/** The household's wallpaper, following live changes. */
export function useHouseholdWallpaper(): WallpaperKey | null {
  const [value, setValue] = useState(current);
  useEffect(() => {
    const update = () => setValue(current);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY && e.key !== null) return;
      current = read();
      update();
    };
    window.addEventListener(EVENT, update);
    window.addEventListener("storage", onStorage);
    update();
    return () => {
      window.removeEventListener(EVENT, update);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return value;
}

/** Draws the texture: `data-wallpaper` on the body while the flag is on and one is built, else none. */
export function useWallpaperBackground(flagOn: boolean): void {
  const wallpaper = shownWallpaper(flagOn, useHouseholdWallpaper());
  useEffect(() => {
    if (wallpaper) document.body.dataset.wallpaper = wallpaper;
    else delete document.body.dataset.wallpaper;
  }, [wallpaper]);
}
